import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import MultiSelect from '../../components/MultiSelect';
import PhysicalReportContent from './PhysicalReportContent';
import { getPlayerPillarScores, getSquadPillarScores } from '../../api';
import { downloadBlob, renderReportPdf } from '../../pdfExport';

// Mounts PhysicalReportContent off-screen for one player, waits for it to settle, captures it,
// then tears the hidden container down. Sequential (one player at a time) rather than mounting
// every selected player at once, to bound how much off-screen DOM/canvas memory is alive.
async function generateOnePlayerPdf(props) {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);
  const root = createRoot(container);
  try {
    await new Promise(resolve => {
      root.render(<PhysicalReportContent {...props} />);
      // Same settle delay MatchChronicPrint.js uses before window.print() — lets images/fonts finish.
      setTimeout(resolve, 700);
    });
    return await renderReportPdf(container);
  } finally {
    root.unmount();
    document.body.removeChild(container);
  }
}

export default function PhysicalReportExportModal({
  currentPlayer, players, opponents, scenarios, customTemplates, positions, allMatchDrills,
  matchOptions, initialMatchIds = [], legacyDescription, onLegacyExport, onClose,
}) {
  const [exportType, setExportType] = useState('current'); // 'current' | 'full'
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([String(currentPlayer.id)]);
  // Defaults to whatever match filter is already active on the page behind this modal, so a
  // trainer who already narrowed the view down to a few matches doesn't have to redo it here.
  const [selectedMatchIds, setSelectedMatchIds] = useState(initialMatchIds.map(String));
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  const selectablePlayers = players.filter(p => !selectedPlayerIds.includes(String(p.id)));
  const removePlayer = id => setSelectedPlayerIds(s => s.filter(v => v !== id));

  const handleGenerate = async () => {
    if (exportType === 'current') { onLegacyExport(); onClose(); return; }
    if (!selectedPlayerIds.length) return;
    setError('');
    setGenerating(true);
    try {
      const selectedPlayers = selectedPlayerIds
        .map(id => players.find(p => String(p.id) === id))
        .filter(Boolean);

      // Fetched once for the whole batch — same squad-wide distribution every selected
      // player's Section 5 quartile is measured against, not re-fetched per player.
      let squadPhysicalScores = [];
      try {
        const squadRows = await getSquadPillarScores('season');
        squadPhysicalScores = (squadRows || []).map(r => r.physical).filter(v => v != null);
      } catch { /* leave empty — Section 5 just omits the quartile line */ }

      const pdfs = [];
      for (let i = 0; i < selectedPlayers.length; i++) {
        const p = selectedPlayers[i];
        setProgress(`Generating report ${i + 1} of ${selectedPlayers.length}…`);

        let physicalScore = null;
        try {
          const scoreRow = await getPlayerPillarScores(p.id, 'season');
          physicalScore = scoreRow?.physical ?? null;
        } catch { /* leave null — Section 5 shows "no data" */ }

        const arrayBuffer = await generateOnePlayerPdf({
          player: p, players, opponents, scenarios, customTemplates, positions, allMatchDrills,
          matchIds: selectedMatchIds, physicalScore, squadPhysicalScores,
        });
        pdfs.push({ player: p, arrayBuffer });
      }

      if (pdfs.length === 1) {
        const blob = new Blob([pdfs[0].arrayBuffer], { type: 'application/pdf' });
        downloadBlob(blob, `${pdfs[0].player.name.replace(/\s+/g, '-').toLowerCase()}-physical-report.pdf`);
      } else {
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        pdfs.forEach(({ player: p, arrayBuffer }) => {
          zip.file(`${p.name.replace(/\s+/g, '-').toLowerCase()}-physical-report.pdf`, arrayBuffer);
        });
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        downloadBlob(zipBlob, `physical-reports-${new Date().toISOString().slice(0, 10)}.zip`);
      }

      onClose();
    } catch (err) {
      console.error('Physical report generation failed:', err);
      setError('Report generation failed — please try again.');
    } finally {
      setGenerating(false);
      setProgress('');
    }
  };

  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16 };
  const inp = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: '0.83rem', fontFamily: 'inherit', outline: 'none' };
  const label = { fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, display: 'block' };
  const radioRow = { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ ...card, width: '100%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 40px 80px rgba(0,0,0,0.6)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>Export PDF</div>
          <button onClick={onClose} disabled={generating} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: generating ? 'not-allowed' : 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 22px' }}>
          <label style={label}>Export type</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
            <label style={{ ...radioRow, borderColor: exportType === 'current' ? '#3b82f6' : 'var(--border-strong)' }}>
              <input type="radio" checked={exportType === 'current'} onChange={() => setExportType('current')} style={{ accentColor: '#3b82f6' }} />
              <div>
                <div style={{ fontWeight: 600 }}>Current view</div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{legacyDescription || `Match Chronic for ${currentPlayer.name}, as shown today`}</div>
              </div>
            </label>
            <label style={{ ...radioRow, borderColor: exportType === 'full' ? '#3b82f6' : 'var(--border-strong)' }}>
              <input type="radio" checked={exportType === 'full'} onChange={() => setExportType('full')} style={{ accentColor: '#3b82f6' }} />
              <div>
                <div style={{ fontWeight: 600 }}>Full Physical Report</div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>All 5 sections — Match Chronic, Match Performance, conclusions, Physical score</div>
              </div>
            </label>
          </div>

          {exportType === 'full' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ ...label, marginBottom: 0 }}>Players</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                  <input
                    type="checkbox"
                    checked={selectedPlayerIds.length === players.length}
                    onChange={() => setSelectedPlayerIds(
                      selectedPlayerIds.length === players.length ? [String(currentPlayer.id)] : players.map(p => String(p.id))
                    )}
                    style={{ accentColor: '#3b82f6' }}
                  />
                  Select all ({players.length})
                </label>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                {selectedPlayerIds.map(id => {
                  const p = players.find(pl => String(pl.id) === id);
                  if (!p) return null;
                  return (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, background: 'var(--bg-app)' }}>
                      <span style={{ flex: 1, fontSize: '0.83rem', color: 'var(--text-primary)' }}>{p.name}</span>
                      {selectedPlayerIds.length > 1 && (
                        <button onClick={() => removePlayer(id)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                      )}
                    </div>
                  );
                })}
              </div>
              {selectablePlayers.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <select
                    value=""
                    onChange={e => { if (e.target.value) setSelectedPlayerIds(s => [...s, e.target.value]); }}
                    style={{ ...inp, width: '100%' }}>
                    <option value="">Add a player…</option>
                    {selectablePlayers.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                  </select>
                </div>
              )}

              <label style={label}>Matches</label>
              <div style={{ marginBottom: 8 }}>
                <MultiSelect options={matchOptions} selected={selectedMatchIds} onChange={setSelectedMatchIds} placeholder="All matches" />
              </div>
              <div style={{ fontSize: '0.74rem', color: 'var(--text-dimmed)', marginBottom: 4 }}>
                Applies to all 3 Match Chronic / Performance sections, for every selected player.
              </div>
            </>
          )}

          {error && <div style={{ marginTop: 12, fontSize: '0.8rem', color: '#f87171' }}>{error}</div>}
          {generating && progress && <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{progress}</div>}
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={generating} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: generating ? 'not-allowed' : 'pointer', fontSize: '0.83rem', fontFamily: 'inherit' }}>
            Cancel
          </button>
          <button
            disabled={generating || (exportType === 'full' && !selectedPlayerIds.length)}
            onClick={handleGenerate}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', fontFamily: 'inherit', fontSize: '0.83rem', fontWeight: 600,
              cursor: generating ? 'not-allowed' : 'pointer',
              background: generating ? 'var(--border)' : '#2563eb', color: generating ? 'var(--text-dimmed)' : '#fff' }}>
            {generating ? 'Generating…' : exportType === 'current' ? 'Export' : 'Generate Report'}
          </button>
        </div>
      </div>
    </div>
  );
}
